import { useCallback, useEffect, useMemo, useState, useRef } from 'react';
import {
    Table,
    TableBody,
    TableCell,
    TableContainer,
    TableHead,
    TableRow,
    TablePagination,
    Paper,
    TextField,
    Checkbox,
    Toolbar,
    Typography,
    Button,
    CircularProgress,
    TableSortLabel,
    Box,
} from '@mui/material';
import { GetApp as ExportIcon } from '@mui/icons-material';
import { styled } from '@mui/material/styles';

type SortDirection = 'asc' | 'desc';

/**
 * Column configuration for the WebGrid component.
 *
 * @template Row The type of the data rows.
 *
 * @property key - Unique identifier for the column.
 * @property title - Display title for the column header.
 * @property dataIndex - Key to access data from row objects.
 * @property sortable - Whether the column is sortable.
 * @property filterable - Whether the column is filterable.
 * @property width - Column width in pixels.
 * @property align - Content alignment within the column.
 * @property headerAlign - Header alignment within the column.
 * @property fixed - Whether the column should be sticky positioned ('left' or 'right').
 * @property render - Custom render function for the column content.
 * @property getFilterValue - Optional accessor returning the string a column is filtered against.
 *   Use this when the displayed value differs from the raw cell value (e.g. a boolean rendered
 *   as a tag), so users can filter on what they actually see.
 */
export interface Column<Row = unknown> {
    key: string;
    title: string;
    dataIndex: string;
    sortable?: boolean;
    filterable?: boolean;
    width?: number;
    align?: 'left' | 'center' | 'right';
    headerAlign?: 'left' | 'center' | 'right';
    fixed?: 'left' | 'right';
    // Method syntax enables bivariant parameter checking, matching common React handler typing
    render?(value: unknown, row: Row, rowIndex: number): React.ReactNode;
    getFilterValue?(row: Row): string;
}

type SummaryOperation = 'sum' | 'avg' | 'count' | 'min' | 'max' | 'text';

type ColumnSummaries = Partial<Record<string, SummaryOperation>>;

type RowId = string | number;
type RowIdSet = Set<RowId>;
type RowIdSetOrUpdater = RowIdSet | ((prev: RowIdSet) => RowIdSet);

/** Safely converts an unknown value to string, avoiding [object Object] */
function toSafeString(value: unknown): string {
    if (value == null) return '';
    switch (typeof value) {
        case 'string':
            return value;
        case 'number':
        case 'boolean':
        case 'bigint':
            return String(value);
        default:
            return '';
    }
}

/**
 * Determines whether a row satisfies every active filter. Kept at module scope
 * so the filter/find callbacks below do not add to the component's nesting depth.
 */
function rowMatchesFilters<T>(
    row: T,
    filters: Record<string, string>,
    gridColumns: Column<T>[],
): boolean {
    return Object.entries(filters).every(([key, filterValue]) => {
        if (!filterValue) return true;
        const column = gridColumns.find(col => col.dataIndex === key);
        const rawValue = column?.getFilterValue
            ? column.getFilterValue(row)
            : (row as Record<string, unknown>)[key];
        const cellValue = toSafeString(rawValue).toLowerCase();
        return cellValue.includes(filterValue.toLowerCase());
    });
}

const StyledTableContainer = styled(TableContainer)(({ theme }) => ({
    maxHeight: 600,
    '& .MuiTableCell-head': {
        backgroundColor: theme.palette.grey[50],
        fontWeight: 600,
        fontSize: '0.75rem',
        borderBottom: `2px solid ${theme.palette.divider}`,
        borderRight: `1px solid ${theme.palette.divider}`,
    },
    /*
     * Data-row backgrounds — stripe, hover and selection — come from the shared
     * `styles/tableRows.css`, which every table in the application uses. The rule that
     * used to live here coloured *every* row on hover, header and filter rows included,
     * and gave them all a pointer cursor whether or not the row did anything; the cursor
     * is now set per row, from the handlers that row actually has.
     */
    '& .MuiTableCell-root': {
        padding: '4px 8px',
        fontSize: '0.75rem',
        borderRight: `1px solid ${theme.palette.divider}`,
        '&:last-child': {
            borderRight: 'none',
        },
    },
}));

const FilterRow = styled(TableRow)(({ theme }) => ({
    backgroundColor: theme.palette.grey[50],
    '& .MuiTableCell-root': {
        padding: '2px 8px',
        fontSize: '0.75rem',
        borderBottom: `1px solid ${theme.palette.divider}`,
        borderRight: `1px solid ${theme.palette.divider}`,
        '&:last-child': {
            borderRight: 'none',
        },
    },
    '& .MuiTextField-root': {
        width: '100%',
        '& .MuiInputBase-root': {
            fontSize: '0.6875rem',
            height: '26px',
        },
    },
}));

const SummaryRow = styled(TableRow)(({ theme }) => ({
    backgroundColor: theme.palette.grey[100],
    '& .MuiTableCell-root': {
        fontWeight: 600,
        fontSize: '0.75rem',
        borderTop: `2px solid ${theme.palette.divider}`,
        borderRight: `1px solid ${theme.palette.divider}`,
        '&:last-child': {
            borderRight: 'none',
        },
    },
}));

/**
 * Props for the `WebGrid` component.
 *
 * @template T The type of the data rows.
 *
 * @remarks
 * This interface defines the configuration and behavior of the `WebGrid` component,
 * including data, columns, filtering, pagination, selection, exporting, and more.
 *
 * The `onPageSizeChange` prop allows consumers to handle changes to the page size
 * when pagination is enabled.
 *
 * @property data - The array of data rows to display in the grid.
 * @property columns - The column definitions for the grid.
 * @property filterBar - Whether to show the filter bar above the grid.
 * @property footer - Whether to show the grid footer.
 * @property columnSummaries - Optional summaries for columns.
 * @property onRowClick - Callback invoked when a row is clicked.
 * @property onRowDoubleClick - Callback invoked when a row is double-clicked.
 * @property onFilterChange - Callback invoked when filters are changed.
 * @property className - Additional CSS class name(s) for the grid container.
 * @property pagination - Whether to enable pagination.
 * @property pageSize - The number of rows per page when pagination is enabled.
 * @property onPageSizeChange - Callback invoked when the page size changes.
 * @property sortable - Whether columns are sortable.
 * @property selectable - Whether rows are selectable.
 * @property multiSelect - Whether multiple rows can be selected.
 * @property loading - Whether the grid is in a loading state.
 * @property emptyMessage - Message to display when there are no rows.
 * @property exportable - Whether the grid supports exporting data.
 * @property onExport - Callback invoked when data is exported.
 * @property height - The height of the grid (number or CSS string).
 * @property storageKey - Key for storing grid state (e.g., column order) in local storage.
 * @property rowIdAccessor - Function to extract a unique ID for each row.
 * @property showPaginationRow - Whether to show the pagination row.
 * @property showRecordCountRow - Whether to show the record count row.
 * @property title - Optional title for the grid.
 */
export type WebGridProps<T = unknown> = {
    data: T[];
    columns?: Column<T>[];
    filterBar?: boolean;
    footer?: boolean;
    columnSummaries?: ColumnSummaries;
    onRowClick?: (row: T, rowIndex?: number) => void;
    onRowDoubleClick?: (row: T, rowIndex?: number) => void;
    onRowContextMenu?: (event: React.MouseEvent, row: T, rowIndex?: number) => void;
    onFilterChange?: (filters: Record<string, string>) => void;
    initialFilters?: Record<string, string>;
    className?: string;
    pagination?: boolean;
    pageSize?: number;
    onPageSizeChange?: (newPageSize: number) => void;
    sortable?: boolean;
    selectable?: boolean;
    multiSelect?: boolean;
    /**
     * Predicate deciding whether a given row may be selected. Rows for which this
     * returns `false` render a disabled checkbox and are ignored by row-click
     * selection and the select-all header checkbox. When omitted every row is
     * selectable.
     */
    isRowSelectable?: (row: T) => boolean;
    loading?: boolean;
    emptyMessage?: string;
    exportable?: boolean;
    onExport?: (rows: T[], columns: Column<T>[]) => void;
    height?: number | string;
    storageKey?: string;
    rowIdAccessor?: (row: T, index: number) => string | number;
    showPaginationRow?: boolean;
    showRecordCountRow?: boolean;
    title?: string;
    /** Controlled selection: array of currently selected rows */
    selectedRows?: T[];
    /** Callback when selection changes */
    onSelectionChange?: (selectedRows: T[]) => void;
    /** Auto-fit column widths to the widest content in each column */
    autoFitColumns?: boolean;
    /** Whether to show checkbox column when selectable is true. Defaults to true. */
    showCheckboxes?: boolean;
    /** Optional callback to compute inline styles for a row. */
    getRowStyle?: (row: T) => React.CSSProperties | undefined;
};

const WebGrid = <T,>({
    data = [] as T[],
    columns = [] as Column<T>[],
    filterBar = true,
    footer = false,
    columnSummaries = {},
    onRowClick,
    onRowDoubleClick,
    onRowContextMenu,
    onFilterChange,
    initialFilters,
    className = 'webgrid',
    pagination = false,
    pageSize = 10,
    onPageSizeChange,
    sortable = true,
    selectable = false,
    multiSelect = false,
    isRowSelectable,
    loading = false,
    emptyMessage = 'No data available',
    exportable = false,
    onExport,
    height,
    storageKey: storageKeyProp,
    rowIdAccessor,
    showPaginationRow = true,
    showRecordCountRow = true,
    title,
    selectedRows: controlledSelectedRows,
    onSelectionChange,
    autoFitColumns = false,
    showCheckboxes = true,
    getRowStyle,
}: WebGridProps<T>) => {
    const [filters, setFilters] = useState<Record<string, string>>(initialFilters ?? {});
    const [sortConfig, setSortConfig] = useState<{ key: string | null; direction: SortDirection }>({
        key: null,
        direction: 'asc',
    });
    // MUI uses 0-based pages
    const [currentPage, setCurrentPage] = useState<number>(0);
    const [internalPageSize, setInternalPageSize] = useState<number>(pageSize);

    // Sync filters when initialFilters prop changes externally
    useEffect(() => {
        if (initialFilters && Object.keys(initialFilters).length > 0) {
            setFilters(initialFilters);
            setCurrentPage(0);
        }
    }, [initialFilters]);

    // Column width management
    const storageKey = storageKeyProp ?? `webgrid-column-widths-${title ?? 'default'}`;
    const [columnWidths, setColumnWidths] = useState<Record<string, number>>(() => {
        try {
            const stored = localStorage.getItem(storageKey);
            return stored ? JSON.parse(stored) : {};
        } catch {
            return {};
        }
    });
    const resizingColumn = useRef<string | null>(null);

    // Save column widths to localStorage when they change
    useEffect(() => {
        try {
            localStorage.setItem(storageKey, JSON.stringify(columnWidths));
        } catch {
            // ignore storage errors
        }
    }, [columnWidths, storageKey]);

    // Use internal page size when no callback provided, otherwise use prop
    const effectivePageSize = onPageSizeChange ? pageSize : internalPageSize;

    // Update internal page size when prop changes
    useEffect(() => {
        if (!onPageSizeChange) {
            setInternalPageSize(pageSize);
        }
    }, [pageSize, onPageSizeChange]);

    // Reset to first page when data changes (e.g., when filtering by different client)
    useEffect(() => {
        setCurrentPage(0);
    }, [data.length]);

    // Selection state management - supports both controlled and uncontrolled modes
    const [internalSelectedRows, setInternalSelectedRows] = useState<RowIdSet>(new Set());

    // Compute selected row IDs from controlled selectedRows prop
    const controlledSelectedIds = useMemo(() => {
        if (!controlledSelectedRows) return null;
        const ids = new Set<RowId>();
        controlledSelectedRows.forEach((row, idx) => {
            const rowId = rowIdAccessor
                ? rowIdAccessor(row, idx)
                : (((row as Record<string, unknown>).id as RowId | undefined) ?? idx);
            ids.add(rowId);
        });
        return ids;
    }, [controlledSelectedRows, rowIdAccessor]);

    // Use controlled selection if provided, otherwise use internal state
    const selectedRows = controlledSelectedIds ?? internalSelectedRows;
    const setSelectedRows = controlledSelectedIds
        ? (newSet: RowIdSetOrUpdater) => {
              // When controlled, we need to call onSelectionChange with the actual rows
              const resolvedSet = typeof newSet === 'function' ? newSet(selectedRows) : newSet;
              if (onSelectionChange) {
                  const selectedData = data.filter((row, idx) => {
                      const rowId = rowIdAccessor
                          ? rowIdAccessor(row, idx)
                          : (((row as Record<string, unknown>).id as RowId | undefined) ?? idx);
                      return resolvedSet.has(rowId);
                  });
                  onSelectionChange(selectedData);
              }
          }
        : setInternalSelectedRows;

    // Keep a ref to setSelectedRows so callbacks always use the latest version
    const setSelectedRowsRef = useRef(setSelectedRows);
    setSelectedRowsRef.current = setSelectedRows;

    // Helper function to calculate sticky column positions
    const calculateStickyPositions = useCallback(
        (columns: Column<T>[]) => {
            const positions: Record<string, { left?: number; right?: number }> = {};
            let leftOffset = selectable && showCheckboxes ? 60 : 0;
            let rightOffset = 0;

            // Calculate left sticky positions
            columns.forEach(column => {
                if (column.fixed === 'left') {
                    positions[column.key] = { left: leftOffset };
                    leftOffset += column.width || 150;
                }
            });

            // Calculate right sticky positions (reverse order)
            const rightColumns = columns.filter(col => col.fixed === 'right').reverse();
            rightColumns.forEach(column => {
                positions[column.key] = { right: rightOffset };
                rightOffset += column.width || 150;
            });

            return positions;
        },
        [selectable],
    );

    // Auto-generate columns from data if not provided
    const gridColumns: Column<T>[] = useMemo(() => {
        if (columns.length > 0) return columns;
        if (data.length === 0) return [] as Column<T>[];

        return Object.keys(data[0] as object).map(key => ({
            key,
            title: key.charAt(0).toUpperCase() + key.slice(1),
            dataIndex: key,
            sortable: true,
            filterable: true,
        })) as Column<T>[];
    }, [columns, data]);

    // Calculate sticky positions for fixed columns
    const stickyPositions = useMemo(
        () => calculateStickyPositions(gridColumns),
        [gridColumns, calculateStickyPositions],
    );

    // Calculate minimum table width based on columns
    const minTableWidth = useMemo(() => {
        const baseWidth = selectable ? 60 : 0;
        const columnsWidth = gridColumns.reduce((total, col) => {
            const width = columnWidths[col.dataIndex] || col.width || 150;
            return total + width;
        }, 0);
        const totalWidth = baseWidth + columnsWidth;

        // Always use calculated width to enable proper horizontal scrolling
        // when content exceeds container width
        return Math.max(totalWidth, 400); // Minimum 400px
    }, [gridColumns, selectable, columnWidths]);

    // Apply filters
    const filteredData: T[] = useMemo(() => {
        return data.filter(row => rowMatchesFilters(row, filters, gridColumns));
    }, [data, filters, gridColumns]);

    // Apply sorting
    const sortedData: T[] = useMemo(() => {
        if (!sortConfig.key) return filteredData;

        const sortKey = sortConfig.key;
        return [...filteredData].sort((a, b) => {
            const aVal = (a as Record<string, unknown>)[sortKey];
            const bVal = (b as Record<string, unknown>)[sortKey];

            if (aVal == null && bVal == null) return 0;
            if (aVal == null) return sortConfig.direction === 'asc' ? 1 : -1;
            if (bVal == null) return sortConfig.direction === 'asc' ? -1 : 1;

            if (typeof aVal === 'number' && typeof bVal === 'number') {
                return sortConfig.direction === 'asc' ? aVal - bVal : bVal - aVal;
            }

            const aStr = toSafeString(aVal);
            const bStr = toSafeString(bVal);
            const comparison = aStr.localeCompare(bStr);
            return sortConfig.direction === 'asc' ? comparison : -comparison;
        });
    }, [filteredData, sortConfig]);

    // Paginate sorted data. A non-positive page size (e.g. the "All" option,
    // which MUI represents as -1) means show every row on a single page.
    const paginatedData: T[] = useMemo(() => {
        if (!pagination || effectivePageSize <= 0) return sortedData;

        const startIndex = currentPage * effectivePageSize;
        return sortedData.slice(startIndex, startIndex + effectivePageSize);
    }, [sortedData, pagination, currentPage, effectivePageSize]);

    // Calculate summary values
    const summaryValues: Record<string, number | string> = useMemo(() => {
        const summaries: Record<string, number | string> = {};
        Object.entries(columnSummaries as Record<string, SummaryOperation | undefined>).forEach(
            ([key, operation]) => {
                const values = filteredData.map(
                    row => Number((row as Record<string, unknown>)[key]) || 0,
                );

                switch (operation) {
                    case 'sum':
                        summaries[key] = values.reduce((sum, val) => sum + val, 0);
                        break;
                    case 'avg':
                        summaries[key] =
                            values.length > 0
                                ? (
                                      values.reduce((sum, val) => sum + val, 0) / values.length
                                  ).toFixed(2)
                                : 0;
                        break;
                    case 'count':
                        summaries[key] = values.length;
                        break;
                    case 'min':
                        summaries[key] = values.length > 0 ? Math.min(...values) : 0;
                        break;
                    case 'max':
                        summaries[key] = values.length > 0 ? Math.max(...values) : 0;
                        break;
                    default:
                        summaries[key] = '';
                }
            },
        );
        return summaries;
    }, [filteredData, columnSummaries]);

    // Handle filter change
    const handleFilterChange = useCallback(
        (key: string, value: string) => {
            const newFilters = {
                ...filters,
                [key]: value,
            };
            setFilters(newFilters);
            setCurrentPage(0); // Reset to first page when filtering

            // Notify parent component of filter changes
            onFilterChange?.(newFilters);
        },
        [filters, onFilterChange],
    );

    // Handle sort
    const handleSort = useCallback((key: string) => {
        setSortConfig(prev => ({
            key,
            direction: prev.key === key && prev.direction === 'asc' ? 'desc' : 'asc',
        }));
    }, []);

    // Handle row selection
    const canSelectRow = useCallback(
        (row: T) => (isRowSelectable ? isRowSelectable(row) : true),
        [isRowSelectable],
    );

    const handleRowSelect = useCallback((rowId: RowId, selected: boolean) => {
        setSelectedRowsRef.current(prev => {
            const newSet = new Set(prev);
            if (selected) {
                newSet.add(rowId);
            } else {
                newSet.delete(rowId);
            }
            return newSet;
        });
    }, []);

    // Handle column resize
    const handleResizeStart = useCallback(
        (dataIndex: string, e: React.MouseEvent) => {
            e.preventDefault();
            e.stopPropagation();
            const startX = e.clientX;
            const startWidth = columnWidths[dataIndex] || 150;
            resizingColumn.current = dataIndex;

            const onMouseMove = (moveEvent: MouseEvent) => {
                const newWidth = startWidth + moveEvent.clientX - startX;
                setColumnWidths(prev => ({
                    ...prev,
                    [dataIndex]: Math.max(newWidth, 40), // Minimum width of 40px
                }));
            };

            const onMouseUp = () => {
                document.removeEventListener('mousemove', onMouseMove);
                document.removeEventListener('mouseup', onMouseUp);
                resizingColumn.current = null;
            };

            document.addEventListener('mousemove', onMouseMove);
            document.addEventListener('mouseup', onMouseUp);
        },
        [columnWidths],
    );

    // Handle select all
    const handleSelectAll = useCallback(
        (selected: boolean) => {
            if (selected) {
                const allIds = paginatedData
                    .map((row, idx) => ({
                        row,
                        id: rowIdAccessor
                            ? rowIdAccessor(row, idx)
                            : (((row as Record<string, unknown>).id as RowId | undefined) ?? idx),
                    }))
                    .filter(({ row }) => canSelectRow(row))
                    .map(({ id }) => id);
                setSelectedRowsRef.current(new Set(allIds));
            } else {
                setSelectedRowsRef.current(new Set());
            }
        },
        [paginatedData, rowIdAccessor, canSelectRow],
    );

    // Auto-fit column widths by measuring text content
    const autoFitWidths = useMemo(() => {
        if (!autoFitColumns || gridColumns.length === 0) return {};

        const canvas = document.createElement('canvas');
        const context = canvas.getContext('2d');
        if (!context) return {};

        const headerFont = 'bold 0.75rem "Roboto", "Helvetica", "Arial", sans-serif';
        const cellFont = '0.75rem "Roboto", "Helvetica", "Arial", sans-serif';
        const padding = 32; // 8px padding on each side + sort icon / resize handle space

        const widths: Record<string, number> = {};

        for (const column of gridColumns) {
            // Measure header text
            context.font = headerFont;
            let maxWidth = context.measureText(column.title).width;

            // Measure data values
            context.font = cellFont;
            for (const row of data) {
                const value = (row as Record<string, unknown>)[column.dataIndex];
                const text = toSafeString(value);
                const measured = context.measureText(text).width;
                if (measured > maxWidth) maxWidth = measured;
            }

            widths[column.dataIndex] = Math.ceil(maxWidth + padding);
        }

        return widths;
    }, [autoFitColumns, gridColumns, data]);

    // Get column width (from state or default)
    const getColumnWidth = useCallback(
        (column: Column<T>) => {
            if (columnWidths[column.dataIndex]) return columnWidths[column.dataIndex];
            if (autoFitColumns && autoFitWidths[column.dataIndex])
                return autoFitWidths[column.dataIndex];
            return column.width || 150;
        },
        [columnWidths, autoFitColumns, autoFitWidths],
    );

    // Handle export
    const handleExport = useCallback(() => {
        if (onExport) {
            onExport(filteredData, gridColumns);
        } else {
            // Default CSV export
            const csvContent = [
                gridColumns.map(col => col.title).join(','),
                ...filteredData.map(row =>
                    gridColumns
                        .map(col => {
                            const v = (row as Record<string, unknown>)[col.dataIndex];
                            return `"${toSafeString(v)}"`;
                        })
                        .join(','),
                ),
            ].join('\n');

            const blob = new Blob([csvContent], { type: 'text/csv' });
            const url = URL.createObjectURL(blob);
            const a = document.createElement('a');
            a.href = url;
            a.download = 'webgrid-export.csv';
            a.click();
            URL.revokeObjectURL(url);
        }
    }, [filteredData, gridColumns, onExport]);

    const totalRecords = sortedData.length;
    const showingAll = effectivePageSize <= 0;
    const firstRecordWhenShowingAll = totalRecords > 0 ? 1 : 0;
    const startRecord = showingAll
        ? firstRecordWhenShowingAll
        : currentPage * effectivePageSize + 1;
    const endRecord = showingAll
        ? totalRecords
        : Math.min((currentPage + 1) * effectivePageSize, totalRecords);

    const renderBodyContent = (): React.ReactNode => {
        if (loading) {
            return (
                <TableRow>
                    <TableCell
                        colSpan={gridColumns.length + (selectable ? 1 : 0)}
                        align="center"
                        sx={{ py: 4 }}
                    >
                        <CircularProgress size={40} />
                    </TableCell>
                </TableRow>
            );
        }

        if (paginatedData.length === 0) {
            return (
                <TableRow>
                    <TableCell
                        colSpan={gridColumns.length + (selectable ? 1 : 0)}
                        align="center"
                        sx={{ py: 4 }}
                    >
                        <Typography variant="body2" color="textSecondary">
                            {emptyMessage}
                        </Typography>
                    </TableCell>
                </TableRow>
            );
        }

        return paginatedData.map((row, rowIndex) => {
            const rowId = rowIdAccessor
                ? rowIdAccessor(row, rowIndex)
                : (((row as Record<string, unknown>).id as RowId | undefined) ?? rowIndex);
            const isSelected = selectedRows.has(rowId);
            const rowSelectable = canSelectRow(row);
            const customRowStyle = getRowStyle ? getRowStyle(row) : undefined;

            return (
                <TableRow
                    key={String(rowId)}
                    hover
                    selected={isSelected}
                    /* A row with a colour of its own keeps it: the shared stripe skips it. */
                    className={customRowStyle?.backgroundColor ? 'table-row--custom-bg' : undefined}
                    onClick={() => {
                        if (selectable && !multiSelect && rowSelectable) {
                            handleRowSelect(rowId, !isSelected);
                        }
                        onRowClick?.(row, rowIndex);
                    }}
                    onDoubleClick={() => onRowDoubleClick?.(row, rowIndex)}
                    onContextMenu={e => {
                        if (onRowContextMenu) {
                            e.preventDefault();
                            onRowContextMenu(e, row, rowIndex);
                        }
                    }}
                    sx={{
                        cursor:
                            onRowClick || onRowDoubleClick || onRowContextMenu
                                ? 'pointer'
                                : 'default',
                        ...customRowStyle,
                    }}
                >
                    {selectable && showCheckboxes && (
                        <TableCell
                            padding="checkbox"
                            sx={{
                                width: 60,
                                minWidth: 60,
                                maxWidth: 60,
                                position: 'sticky',
                                left: 0,
                                backgroundColor: 'inherit',
                                zIndex: 1,
                            }}
                        >
                            <Checkbox
                                checked={isSelected}
                                disabled={!rowSelectable}
                                onChange={e => handleRowSelect(rowId, e.target.checked)}
                                onClick={e => e.stopPropagation()}
                            />
                        </TableCell>
                    )}
                    {gridColumns.map(column => {
                        const stickyPosition = stickyPositions[column.key];
                        const isFixed = column.fixed === 'left' || column.fixed === 'right';
                        const columnWidth = getColumnWidth(column);

                        return (
                            <TableCell
                                key={`${String(rowId)}-${column.key}`}
                                align={column.align || 'left'}
                                sx={{
                                    width: columnWidth,
                                    minWidth: columnWidth,
                                    maxWidth: columnWidth,
                                    overflow: 'hidden',
                                    textOverflow: 'ellipsis',
                                    whiteSpace: 'nowrap',
                                    ...(isFixed && {
                                        position: 'sticky',
                                        left: stickyPosition?.left,
                                        right: stickyPosition?.right,
                                        backgroundColor: 'inherit',
                                        zIndex: 1,
                                    }),
                                }}
                            >
                                {column.render
                                    ? column.render(
                                          (row as Record<string, unknown>)[column.dataIndex],
                                          row,
                                          rowIndex,
                                      )
                                    : toSafeString(
                                          (row as Record<string, unknown>)[column.dataIndex],
                                      )}
                            </TableCell>
                        );
                    })}
                </TableRow>
            );
        });
    };

    return (
        <Paper
            className={className}
            sx={{
                width: '100%',
                height: height,
                display: 'flex',
                flexDirection: 'column',
            }}
        >
            {/* Toolbar */}
            {(showRecordCountRow || exportable || title) && (
                <Toolbar
                    sx={{
                        justifyContent: 'space-between',
                        minHeight: '56px !important',
                        flexShrink: 0,
                    }}
                >
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
                        {title && (
                            <Typography variant="h6" component="h2" sx={{ fontWeight: 600 }}>
                                {title}
                            </Typography>
                        )}
                        {showRecordCountRow && (
                            <Typography variant="body2" color="textSecondary">
                                {totalRecords > 0
                                    ? `Showing ${startRecord}-${endRecord} of ${totalRecords} records`
                                    : 'No records found'}
                            </Typography>
                        )}
                    </Box>
                    {!showRecordCountRow && !title && <Box />}
                    {exportable && (
                        <Button
                            startIcon={<ExportIcon />}
                            onClick={handleExport}
                            variant="outlined"
                            size="small"
                        >
                            Export
                        </Button>
                    )}
                </Toolbar>
            )}

            {/* Fixed Header and Filter Bar */}
            <StyledTableContainer
                sx={{
                    flex: 1,
                    overflow: 'auto',
                    '&::-webkit-scrollbar': {
                        width: '8px',
                        height: '8px',
                    },
                    '&::-webkit-scrollbar-track': {
                        backgroundColor: '#f1f1f1',
                    },
                    '&::-webkit-scrollbar-thumb': {
                        backgroundColor: '#c1c1c1',
                        borderRadius: '4px',
                    },
                    '&::-webkit-scrollbar-thumb:hover': {
                        backgroundColor: '#a8a8a8',
                    },
                }}
            >
                <Table
                    size="small"
                    sx={{
                        minWidth: minTableWidth,
                        tableLayout: 'fixed', // Use fixed layout for consistent column widths
                    }}
                >
                    <TableHead
                        sx={{
                            position: 'sticky',
                            top: 0,
                            zIndex: 10,
                            backgroundColor: 'inherit',
                        }}
                    >
                        <TableRow>
                            {selectable && showCheckboxes && (
                                <TableCell
                                    padding="checkbox"
                                    sx={{
                                        width: 60,
                                        minWidth: 60,
                                        maxWidth: 60,
                                        position: 'sticky',
                                        left: 0,
                                        backgroundColor: 'inherit',
                                        zIndex: 13,
                                    }}
                                >
                                    {multiSelect &&
                                        (() => {
                                            const selectableCount = paginatedData.filter(row =>
                                                canSelectRow(row),
                                            ).length;
                                            return (
                                                <Checkbox
                                                    indeterminate={
                                                        selectedRows.size > 0 &&
                                                        selectedRows.size < selectableCount
                                                    }
                                                    checked={
                                                        selectableCount > 0 &&
                                                        selectedRows.size === selectableCount
                                                    }
                                                    onChange={e =>
                                                        handleSelectAll(e.target.checked)
                                                    }
                                                />
                                            );
                                        })()}
                                </TableCell>
                            )}
                            {gridColumns.map(column => {
                                const stickyPosition = stickyPositions[column.key];
                                const isFixed = column.fixed === 'left' || column.fixed === 'right';
                                const columnWidth = getColumnWidth(column);

                                return (
                                    <TableCell
                                        key={column.key}
                                        align={column.headerAlign || 'left'}
                                        sx={{
                                            width: columnWidth,
                                            minWidth: columnWidth,
                                            maxWidth: columnWidth,
                                            fontWeight: 'bold',
                                            overflow: 'hidden',
                                            textOverflow: 'ellipsis',
                                            whiteSpace: 'nowrap',
                                            position: 'relative',
                                            ...(isFixed && {
                                                position: 'sticky',
                                                left: stickyPosition?.left,
                                                right: stickyPosition?.right,
                                                backgroundColor: 'inherit',
                                                zIndex: 12,
                                            }),
                                        }}
                                    >
                                        <Box
                                            sx={{
                                                display: 'flex',
                                                alignItems: 'center',
                                                height: '100%',
                                            }}
                                        >
                                            {sortable && column.sortable !== false ? (
                                                <TableSortLabel
                                                    active={sortConfig.key === column.dataIndex}
                                                    direction={
                                                        sortConfig.key === column.dataIndex
                                                            ? sortConfig.direction
                                                            : 'asc'
                                                    }
                                                    onClick={() => handleSort(column.dataIndex)}
                                                    sx={{ flex: 1 }}
                                                >
                                                    {column.title}
                                                </TableSortLabel>
                                            ) : (
                                                <Box sx={{ flex: 1 }}>{column.title}</Box>
                                            )}
                                            {/* Resize handle */}
                                            <Box
                                                sx={{
                                                    position: 'absolute',
                                                    top: 0,
                                                    right: 0,
                                                    width: 5,
                                                    height: '100%',
                                                    cursor: 'col-resize',
                                                    userSelect: 'none',
                                                    backgroundColor: 'transparent',
                                                    '&:hover': {
                                                        backgroundColor: 'rgba(0, 0, 0, 0.1)',
                                                    },
                                                }}
                                                onMouseDown={e =>
                                                    handleResizeStart(column.dataIndex, e)
                                                }
                                            />
                                        </Box>
                                    </TableCell>
                                );
                            })}
                        </TableRow>
                        {filterBar && (
                            <FilterRow>
                                {selectable && showCheckboxes && (
                                    <TableCell
                                        sx={{
                                            width: 60,
                                            minWidth: 60,
                                            maxWidth: 60,
                                            position: 'sticky',
                                            left: 0,
                                            backgroundColor: 'inherit',
                                            zIndex: 13,
                                        }}
                                    />
                                )}
                                {gridColumns.map(column => {
                                    const stickyPosition = stickyPositions[column.key];
                                    const isFixed =
                                        column.fixed === 'left' || column.fixed === 'right';
                                    const columnWidth = getColumnWidth(column);

                                    return (
                                        <TableCell
                                            key={`filter-${column.key}`}
                                            sx={{
                                                width: columnWidth,
                                                minWidth: columnWidth,
                                                maxWidth: columnWidth,
                                                overflow: 'hidden',
                                                ...(isFixed && {
                                                    position: 'sticky',
                                                    left: stickyPosition?.left,
                                                    right: stickyPosition?.right,
                                                    backgroundColor: 'inherit',
                                                    zIndex: 12,
                                                }),
                                            }}
                                        >
                                            {column.filterable !== false && (
                                                <TextField
                                                    size="small"
                                                    placeholder={`Filter ${column.title}...`}
                                                    value={filters[column.dataIndex] ?? ''}
                                                    onChange={e =>
                                                        handleFilterChange(
                                                            column.dataIndex,
                                                            e.target.value,
                                                        )
                                                    }
                                                    variant="outlined"
                                                    sx={{ width: '100%' }}
                                                />
                                            )}
                                        </TableCell>
                                    );
                                })}
                            </FilterRow>
                        )}
                    </TableHead>
                    <TableBody>{renderBodyContent()}</TableBody>
                    {footer && (
                        <tfoot>
                            <SummaryRow>
                                {selectable && (
                                    <TableCell
                                        sx={{
                                            width: 60,
                                            minWidth: 60,
                                            maxWidth: 60,
                                            position: 'sticky',
                                            left: 0,
                                            backgroundColor: 'inherit',
                                            zIndex: 1,
                                        }}
                                    />
                                )}
                                {gridColumns.map(column => {
                                    const stickyPosition = stickyPositions[column.key];
                                    const isFixed =
                                        column.fixed === 'left' || column.fixed === 'right';
                                    const columnWidth = getColumnWidth(column);

                                    return (
                                        <TableCell
                                            key={`summary-${column.key}`}
                                            align={column.align || 'left'}
                                            sx={{
                                                width: columnWidth,
                                                minWidth: columnWidth,
                                                maxWidth: columnWidth,
                                                overflow: 'hidden',
                                                textOverflow: 'ellipsis',
                                                whiteSpace: 'nowrap',
                                                ...(isFixed && {
                                                    position: 'sticky',
                                                    left: stickyPosition?.left,
                                                    right: stickyPosition?.right,
                                                    backgroundColor: 'inherit',
                                                    zIndex: 1,
                                                }),
                                            }}
                                        >
                                            {summaryValues[column.dataIndex] ?? ''}
                                        </TableCell>
                                    );
                                })}
                            </SummaryRow>
                        </tfoot>
                    )}
                </Table>
            </StyledTableContainer>

            {/* Pagination */}
            {pagination && showPaginationRow && (
                <TablePagination
                    component="div"
                    count={totalRecords}
                    page={currentPage}
                    onPageChange={(_, newPage) => setCurrentPage(newPage)}
                    rowsPerPage={effectivePageSize}
                    onRowsPerPageChange={event => {
                        const newPageSize = parseInt(event.target.value, 10);
                        setCurrentPage(0);
                        if (onPageSizeChange) {
                            onPageSizeChange(newPageSize);
                        } else {
                            setInternalPageSize(newPageSize);
                        }
                    }}
                    // 20 is the page size most callers pass (Clients, Contracts, Goods
                    // Sale, Inventory, Storno). Without it here MUI cannot match the
                    // value to an option, so it warns and renders the dropdown blank.
                    rowsPerPageOptions={[5, 10, 20, 25, 50, 100, { label: 'All', value: -1 }]}
                />
            )}
        </Paper>
    );
};

export default WebGrid;
